import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";


import axios from "axios";
import "./App.css";
import { getNames } from "country-list";
import Swal from "sweetalert2";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/e-invoice').replace(/\/$/, '');

/**
 * Escapes HTML metacharacters for safe interpolation into innerHTML sinks
 * (SweetAlert2 html: templates). F1: defence-in-depth against stored XSS.
 */
export const esc = (s) =>
  String(s ?? "-").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<EntryPage />} />
        <Route path="/form" element={<InvoicePage />} />
        <Route path="/summary" element={<SummaryPage />} />
        <Route path="/locked" element={<LockedPage />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function EntryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("orderid");

  useEffect(() => {
    if (!hash) {
      navigate("/404", { replace: true });
      return;
    }

    axios
      .get(`${API_BASE}/get_booking.php?${encodeURIComponent(hash)}`)
      .then((res) => {
        const data = res.data;

        if (!data.allowed) {
          navigate("/locked", { state: data, replace: true });
          return;
        }

        if (data.alreadyRequested) {
          navigate("/summary", { state: {...data,hash}, replace: true });
          return;
        }

        navigate("/form", { state: {...data,hash}, replace: true });
      })
      .catch(() => navigate("/404", { replace: true }));
  }, [hash, navigate]);

  return <h2 style={{ textAlign: "center" }}>Loading...</h2>;
}



function NotFound() {
  return (
    <div style={{ textAlign: "center", marginTop: "80px" }}>
      <h1>404</h1>
      <p>Page Not Found</p>
    </div>
  );
}

export function InvoicePage() {
  // const [booking, setBooking] = useState(null); // ⭐ booking details
  const [loading, setLoading] = useState(true); // ⭐
  const [errors, setErrors] = useState({});
  const [useGeneralTin, setUseGeneralTin] = useState(false);
  const [isForeigner, setIsForeigner] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("individual");
  const navigate = useNavigate();
  const { state } = useLocation();

  const order = state?.order || null;


  const GENERAL_TIN = "EI000000000010";
  const FOREIGNER_TIN = "EI000000000020";

  const [form, setForm] = useState({
    user_type: "individual",
    name: "",
    tin: "",
    id_type: "",
    id_num: "",
    sst_num: "NA", // default
    email: "",
    contact: "",
    address1: "",
    address2: "",
    country: "",
    state: "",
    poscode: "",
    city: "",
  });

  // const countries = getNames();
  let countries = getNames()
    .filter(c => c !== "Malaysia");   // remove Malaysia from original list

  countries.unshift("Malaysia"); 
  const malaysiaStates = [
    "Johor","Kedah","Kelantan","Melaka","Negeri Sembilan","Pahang","Pulau Pinang",
    "Perak","Perlis","Sabah","Sarawak","Selangor","Terengganu",
    "Wilayah Persekutuan Kuala Lumpur","Wilayah Persekutuan Labuan","Wilayah Persekutuan Putrajaya",
  ];



  // ⭐ Extract booking_id from URL
  // const hash = searchParams.get("orderid");

  useEffect(() => {
    if (form.user_type === "individual") {
      setForm((prev) => ({
        ...prev,
        id_type: "NRIC",
      }));
    } else if (form.user_type === "company") {
      setForm((prev) => ({
        ...prev,
        id_type: "BRN",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        id_type: "",
      }));
    }
  }, [form.user_type]);

  // ⭐ Load booking details
  const location = useLocation();
  const data = location.state;
  const hash = state?.hash;

  const mode = data?.mode || "submit";
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!data) {
      navigate("/404", { replace: true });
      return;
    }

    // setBooking(data);

    // NEW submission
    if (data.customer) {
      setForm(prev => ({ ...prev, ...data.customer }));
    }

    // EDIT submission (from submitted_invoice)
    if (data.submitted_invoice) {
      const s = data.submitted_invoice;

      setActiveTab(s.user_type); // 👈 IMPORTANT

      setForm(prev => ({
        ...prev,
        user_type: s.user_type,
        name: s.customer_name,
        email: s.customer_email,
        contact: s.customer_phone,
        tin: s.customer_tin,
        id_type: s.id_type,
        id_num: s.customer_brn,
        address1: s.address1,
        address2: s.address2 ?? "",
        city: s.city,
        poscode: s.postcode,
        state: s.state,
        country: s.customer_country
      }));
    }

    setLoading(false);
  }, [data, navigate]);

  // =============================
  // Validation functions (unchanged)
  // =============================
  // Pure: computes the message for one field. No state writes.
  const computeFieldError = (name, value, currentForm) => {
    const v = value ?? "";
    switch (name) {
      case "name":
        if (!/^[A-Za-z\s]+$/.test(v)) return "Only letters allowed";
        break;
      case "tin":
        if (!/^[A-Za-z]{2}\d{12}$/.test(v))
          return "TIN must start with 2 letters followed by 12 digits (e.g. EI000000000010)";
        break;
      case "id_type":
        if (!v) return "Please select ID type";
        break;
      case "id_num":
        if (currentForm.id_type === "NRIC" && !/^\d{12}$/.test(v))
          return "NRIC must be 12 digits";
        if (currentForm.id_type === "PASSPORT" && !/^[A-Za-z0-9]{6,9}$/.test(v))
          return "Passport must be 6–9 alphanumeric characters";
        if (currentForm.id_type === "ARMY" && !/^[A-Za-z0-9]{6,9}$/.test(v))
          return "Army ID must be 6–9 alphanumeric characters";
        if (currentForm.id_type === "BRN" && !/^[A-Za-z0-9/-]+$/.test(v))
          return "BRN allows letters, numbers, - and / only";
        break;
      case "sst_num":
        if (v && !/^[A-Za-z0-9]+$/.test(v)) return "Invalid SST format";
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Invalid email format";
        break;
      case "contact":
        if (!/^\d{10,11}$/.test(v)) return "Must be 10–11 digits";
        break;
      case "address1":
        if (!v.trim()) return "Address required";
        break;
      case "country":
        if (!v) return "Select a country";
        break;
      case "state":
        if (!v) return "Select a state";
        break;
      case "poscode":
        if (!/^\d{5}$/.test(v)) return "Must be 5 digits";
        break;
      case "city":
        if (!/^[A-Za-z\s]+$/.test(v)) return "Only letters allowed";
        break;
      default:
        break;
    }
    return "";
  };

  const validateField = (name, value) => {
    const message = computeFieldError(name, value, form);
    setErrors((prev) => ({ ...prev, [name]: message }));
    return message;
  };

  // H5: validates every field at once. Returns the freshly computed error map
  // rather than relying on the async `errors` state.
  const validateAll = () => {
    const fields = [
      "name", "tin", "id_type", "id_num", "email", "contact",
      "address1", "country", "state", "poscode", "city",
    ];
    if (form.user_type === "company") fields.push("sst_num");

    const nextErrors = {};
    fields.forEach((f) => {
      nextErrors[f] = computeFieldError(f, form[f], form);
    });

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  // ── ID → TIN lookup ────────────────────────────────────────────────────────
  // Fields the backend may return on a DB hit. id_type / id_num are deliberately
  // absent: the customer just typed those, so they are never overwritten.
  const LOOKUP_FIELDS = [
    "name", "tin", "email", "contact",
    "address1", "address2", "city", "poscode", "state", "country",
  ];

  const applyLookupRecord = (record) => {
    setForm((prev) => {
      const next = { ...prev };
      LOOKUP_FIELDS.forEach((field) => {
        const value = record[field];
        if (value !== null && value !== undefined && value !== "") {
          next[field] = value;
        }
      });
      return next;
    });

    // The two TIN checkboxes are a *display* of whether the TIN equals a General
    // TIN constant. Now that the TIN has been replaced with the customer's real
    // one, leaving them ticked would be a lie, so re-derive them from the value.
    const filledTin = record.tin ?? "";
    setUseGeneralTin(filledTin === GENERAL_TIN);
    setIsForeigner(filledTin === FOREIGNER_TIN);

    // The record came from a submission this app previously accepted, so the
    // stale error map from partially-typed input no longer applies.
    setErrors({});
  };

  const lookupCustomer = async () => {
    // Validate locally first — a malformed ID would only earn a 400.
    const idTypeError = computeFieldError("id_type", form.id_type, form);
    const idNumError = computeFieldError("id_num", form.id_num, form);

    if (idTypeError || idNumError) {
      setErrors((prev) => ({ ...prev, id_type: idTypeError, id_num: idNumError }));
      return;
    }

    setLookupLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/inv.php/lookup_customer`, {
        order_token: hash,
        id_type: form.id_type,
        id_value: form.id_num,
      });

      const { source, tin, record } = res.data || {};

      if (source === "db" && record) {
        const confirm = await Swal.fire({
          icon: "question",
          title: "Found a previous record",
          text: "Use your previous details to fill in this form?",
          showCancelButton: true,
          confirmButtonText: "Yes, use it",
          cancelButtonText: "No, I'll type it",
          confirmButtonColor: "#003b67",
          cancelButtonColor: "#d33",
        });

        if (confirm.isConfirmed) {
          applyLookupRecord(record);
        }
        return;
      }

      if (source === "lhdn" && tin) {
        setForm((prev) => ({ ...prev, tin }));
        setErrors((prev) => ({ ...prev, tin: "" }));
        return;
      }

      Swal.fire({
        icon: "info",
        title: "No record found",
        text: "Please fill in your details manually.",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch {
      // A lookup failure must never block the form — the customer can still type.
      Swal.fire({
        icon: "error",
        title: "Lookup unavailable",
        text: "Please fill in your details manually.",
      });
    } finally {
      setLookupLoading(false);
    }
  };

  // ⭐ Submit to backend
  const preview = {
    name: form.name || "-",
    email: form.email || "-",
    contact: form.contact || "-",
    tin: form.tin || "-",
    id_type: form.id_type || "-",
    id_num: form.id_num || "-",
    address1: form.address1 || "-",
    address2: form.address2 || "",
    poscode: form.poscode || "-",
    state: form.state || "-",
    country: form.country || "-"
  };

  const submitINV = async (e) => {
    e.preventDefault();

    const validationErrors = validateAll();
    const hasError = Object.values(validationErrors).some((msg) => msg);
    if (hasError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix errors before submitting."
      });
      return;
    }

    // ⭐ Confirmation popup
    const confirm = await Swal.fire({
      icon: "question",
      title: "Confirm Submission",
      html: `
        <div style="text-align:left">
          <p><b>Name:</b> ${esc(preview.name)}</p>
          <p><b>Email:</b> ${esc(preview.email)}</p>
          <p><b>Contact:</b> ${esc(preview.contact)}</p>
          <p><b>TIN:</b> ${esc(preview.tin)}</p>
          <p><b>${esc(preview.id_type)}:</b> ${esc(preview.id_num)}</p>
          <p><b>Address:</b> ${esc(preview.address1)},<br> ${esc(preview.address2)},<br> ${esc(preview.poscode)}, ${esc(preview.state)}, ${esc(preview.country)}</p>
          <p><b>Amount:</b> RM ${esc(order?.total_payable)}</p>
        </div>
        <br/>
        <p style="color:#c0392b;font-weight:bold">
          Please ensure all information is correct before submitting.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33"
    });

    if (!confirm.isConfirmed) return;   // ⭐ stop if cancelled

    // ⭐ ADD LOADING HERE
    Swal.fire({
      title: "Submitting...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const payload = {
      inv_id: order?.invoice_id,
      cus_tin_brn: {
        TIN: form.tin,
        [form.id_type]: form.id_num
      },
      customer: {
        user_type: form.user_type,
        name: form.name,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        postcode: form.poscode,
        state: form.state,
        country_code: "MYS",
        email: form.email,
        phone: form.contact,
        id_type: form.id_type
      },
      pay_for: formatPaymentType(order?.payment_type),
      amount: order?.total_payable,
    };

    try {
      const res = await axios.post(
        `${API_BASE}/inv.php/submit_json_document`,
        { order_token: hash, mode, ...payload }
      );

      if (res.data?.submissionUid) {
        // H3 (2026-08-04): inv.php now persists the submission itself —
        // a 'pending' row before the LHDN call, updated to 'submitted' after.
        // The old save_inv.php endpoint is deleted (2026-08-04, Fable 5 X-1):
        // it was an unvalidated second write path, so inv.php owns the full
        // persist flow and there is no second call to make.
        Swal.fire({
          icon: "success",
          title: "Submitted Successfully!",
          html: `<p>A copy of the invoice will be sent to your email.</p>`,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(() => {
          window.location.href =
          `${window.location.origin}/?orderid=${encodeURIComponent(hash)}`;
        });

      } else {
        Swal.fire({
          icon: "info",
          title: "Submitted",
          text: "Submission completed, but no submission ID was returned."
        });
      }

    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          err?.response?.data?.message ||
          "Something went wrong while submitting the invoice."
      });
    }
  };



  // ================================
  // Loading State
  // ================================
  if (loading) return <h2>Loading Purchase Details...</h2>;


  const handleTab = (type) => {
    setActiveTab(type);
    setUseGeneralTin(false);
    setIsForeigner(false);
    setForm(prev => ({
      ...prev,
      user_type: type,
      tin: "",
      id_type: "",
      id_num: "", // reset when switching
      sst_num: type === "company" ? "" : "NA", // reset SST
    }));
  };

  const formatPaymentType = (type) => {
    if (!type) return "-";

    return type
      .split("_")                  // split by _
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");                  // join with space
  };

  return (
    <div className="container">
      <header className="header">
        <h1>SPORTS WE PLAY SDN. BHD.</h1>
      </header>

      {/* ⭐ Purchase Details */}
      <div className="card">
        <h2 className="card-title">Purchase Details</h2>
        <p className="card-text">
          Order ID: <b>{order?.order_id || "-"}</b>
        </p>
        <p className="card-text">
          Invoice ID: <b>{order?.invoice_id || "-"}</b>
        </p>
        <p className="card-text">
          Amount: <b>RM {order?.total_payable || "-"}</b>
        </p>
        <p className="card-text">
          Type: <b>{formatPaymentType(order?.payment_type) || "-"}</b>
        </p>
      </div>

      {/* ==================== Buyer Form ==================== */}
      <main className="content">
        <div className="user-type-toggle">
          <button
            type="button"
            className={activeTab === "individual" ? "active" : ""}
            onClick={() => handleTab("individual")}
          >
            Individual
          </button>

          <button
            type="button"
            className={activeTab === "company" ? "active" : ""}
            onClick={() => handleTab("company")}
          >
            Company
          </button>
        </div>

      </main>
      <main className="content">
        
        <form className="form" onSubmit={submitINV}>
          <h2 className="card-title">Buyer Details</h2>

          {isEdit && (
            <div
              className="info"
              style={{
                color: "#c0392b",
                fontWeight: "bold",
                marginBottom: "12px"
              }}
            >
              You are editing a previously submitted invoice
            </div>
          )}

          {/* ── Identification — FIRST (2026-08-08, 龙哥): the ID drives the
                 lookup, which fills in everything below it. ─────────────── */}
          <label>Identification Type</label>
          <select
            name="id_type"
            value={form.id_type}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
            className="select-input"
          >
            <option value="">Select...</option>

            {form.user_type === "individual" && (
              <>
                <option value="NRIC">NRIC</option>
                <option value="PASSPORT">Passport</option>
                <option value="ARMY">Army No.</option>
              </>
            )}

            {form.user_type === "company" && (
              <option value="BRN">Business Registration No.</option>
            )}
          </select>
          {errors.id_type && <span className="error">{errors.id_type}</span>}

          <label>Identification Number</label>
          <div className="id-lookup-row">
            <input
              name="id_num"
              type="text"
              placeholder="Identification Number"
              value={form.id_num}
              onChange={handleChange}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              required
            />
            <button
              type="button"
              className="id-search-btn"
              onClick={lookupCustomer}
              disabled={lookupLoading}
              aria-label="Search for TIN"
              title="Search for TIN"
            >
              {lookupLoading ? (
                <span className="id-search-spinner" />
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
              )}
            </button>
          </div>
          {errors.id_num && <span className="error">{errors.id_num}</span>}
          <span className="info">
            *Enter your ID and press search to auto-fill your details
          </span>

          {/* All your fields (unchanged) */}
          {/* ---------------------------- */}
          {/* Name */}
          <label>
            {form.user_type === "individual"
              ? "Individual Name"
              : "Company Name"}
          </label>
          <input
            name="name"
            type="text"
            placeholder="Name"
            value={form.name}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          <span className="info">
            {form.user_type === "individual"
              ? "*Name as per NRIC/Passport"
              : "*Name as per SSM (For Company)"}
          </span>
          

          {/* TIN */}
          {/* === Use General TIN === */}
          <label>Tax Identification Number (TIN)</label>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            
            {/* "Use General TIN" (EI000000000010) is the Malaysian-individual
                fallback, so it stays individual-only. */}
            {form.user_type === "individual" && (
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={useGeneralTin}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setUseGeneralTin(checked);

                  if (checked) {
                    // Auto fill TIN
                    setForm(prev => ({ ...prev, tin: GENERAL_TIN }));
                    setIsForeigner(false); // optional: uncheck foreigner
                  } else {
                    // Clear TIN if they untick
                    setForm(prev => ({ ...prev, tin: "" }));
                  }
                }}
              />
              <span>Use General TIN</span>
            </label>
            )}

            

            {/* === I AM A FOREIGNER === */}
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={isForeigner}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsForeigner(checked);

                  if (checked) {
                    // Clear TIN
                    setForm(prev => ({ ...prev, tin: FOREIGNER_TIN }));
                    setUseGeneralTin(false); // optional: uncheck general tin
                  } else {
                    // Clear TIN if they untick
                    setForm(prev => ({ ...prev, tin: "" }));
                  }
                }}
              />
              <span>I am a foreigner</span>
            </label>
          </div>

          {/* === TIN INPUT === */}
          <input
            type="text"
            name="tin"
            placeholder="Enter TIN"
            value={form.tin}
            onChange={(e) => {
              handleChange(e);
              setForm(prev => ({ ...prev, tin: e.target.value }));
            }}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
          />


          {errors.tin && <span className="error">{errors.tin}</span>}

          {/* SST */}
          {form.user_type === "company" && (
            <>
            <label>Buyer's SST Registration Number (Mandatory for SST-registrant)</label>
            <input
              name="sst_num"
              type="text"
              placeholder="SST Registration Number"
              value={form.sst_num}
              onChange={handleChange}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
            />
            {errors.sst_num && <span className="error">{errors.sst_num}</span>}
            </>
          )}
          {/* Email */}
          <label>Email</label>
          <input
            name="email"
            type="text"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          {errors.email && <span className="error">{errors.email}</span>}

          {/* Contact */}
          <label>Contact Number</label>
          <input
            name="contact"
            type="text"
            placeholder="Contact Number"
            value={form.contact}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          {errors.contact && <span className="error">{errors.contact}</span>}

          {/* Address */}
          <label>Address Line 1</label>
          <input
            name="address1"
            type="text"
            placeholder="Address Line 1"
            value={form.address1}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          {errors.address1 && <span className="error">{errors.address1}</span>}

          <label>Address Line 2</label>
          <input
            name="address2"
            type="text"
            placeholder="Address Line 2"
            value={form.address2}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
          />

          {/* Country */}
          <label>Country</label>
          <select
            name="country"
            value={form.country}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
            className="select-input"
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          {errors.country && <span className="error">{errors.country}</span>}

          {/* State */}
          <label>{form.country === "Malaysia" ? "State" : "Region / State"}</label>
          {form.country === "Malaysia" ? (
            <select
              name="state"
              value={form.state}
              onChange={handleChange}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              required
              className="select-input"
            >
              <option value="">Select State</option>
              {malaysiaStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          ) : (
            <input
              name="state"
              type="text"
              placeholder="Enter your region or state"
              value={form.state}
              onChange={handleChange}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              required
            />
          )}
          {errors.state && <span className="error">{errors.state}</span>}

          {/* Postal Code */}
          <label>Postal Code</label>
          <input
            name="poscode"
            type="text"
            placeholder="Postal Code"
            value={form.poscode}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          {errors.poscode && <span className="error">{errors.poscode}</span>}

          {/* City */}
          <label>City</label>
          <input
            name="city"
            type="text"
            placeholder="City"
            value={form.city}
            onChange={handleChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            required
          />
          {errors.city && <span className="error">{errors.city}</span>}
          <span></span>
          <span></span>
          <button type="submit">
            {isEdit ? "Update Request" : "Request for E-Invoice"}
          </button>
          <span className="info" style={{ textAlign: "center", fontWeight: "bold" }}>The information you provide will be securely collected and used exclusively for the purpose of processing your request, in full compliance with our Privacy Policy.</span>
        </form>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} SPORTS WE PLAY SDN. BHD.</p>
      </footer>
    </div>
  );
}


function SummaryPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state || !state.submitted_invoice) {
    return <NotFound />;
  }

  const invoice = state.submitted_invoice;
  const canEdit = state.within3Days === true;
  const order = state?.order || null;

  const formatPaymentType = (type) => {
    if (!type) return "-";
    return type
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  return (
    <div className="page-wrapper">
    <div className="container">
      <header className="header">
        <h1>SPORTS WE PLAY SDN. BHD.</h1>
      </header>

      <main className="content">
        <div className="summary-wrapper">
      <div className="card">
        <h2 className="card-title">Your E-Invoice</h2>

        <table className="invoice-table">
          <tbody>
            <tr>
              <td><b>Order ID</b></td>
              <td>{invoice.order_id}</td>
            </tr>
            <tr>
              <td><b>Payment Type</b></td>
              <td>{formatPaymentType(invoice.payment_type)}</td>
            </tr>
            <tr>
              <td><b>Payment Date</b></td>
              <td>{order.update_date_time}</td>
            </tr>
            <tr>
              <td><b>Total Paid</b></td>
              <td>RM {invoice.total_payable}</td>
            </tr>
            <tr>
              <td><b>Name</b></td>
              <td>{invoice.customer_name}</td>
            </tr>
            <tr>
              <td><b>Email</b></td>
              <td>{invoice.customer_email}</td>
            </tr>
            <tr>
              <td><b>Contact</b></td>
              <td>{invoice.customer_phone}</td>
            </tr>
            <tr>
              <td><b>TIN</b></td>
              <td>{invoice.customer_tin}</td>
            </tr>
            <tr>
              <td><b>{invoice.id_type}</b></td>
              <td>{invoice.customer_brn}</td>
            </tr>
            <tr>
              <td><b>Address</b></td>
              <td>
                {invoice.address1}
                {invoice.address2 && `, ${invoice.address2}`},
                {` ${invoice.postcode} ${invoice.city}, ${invoice.state}`}
              </td>
            </tr>
            <tr>
              <td><b>Status</b></td>
              <td>{invoice.status}</td>
            </tr>
            <tr>
              <td><b>Submitted At</b></td>
              <td>{invoice.created_at}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {canEdit && (
        <button
          className="sum_edit_button"
          onClick={() => navigate("/form", { state: {...state, mode: "edit", } })}
        >
          Edit & Resubmit
        </button>
      )}
      </div>
      </main>
      <footer className="footer">
        <p>© {new Date().getFullYear()} SPORTS WE PLAY SDN. BHD.</p>
      </footer>
    </div>
    </div>
  );
}


function LockedPage() {
  const { state } = useLocation();

  const invoice = state?.submitted_invoice || null;
  const order = state?.order || null;

  const hasSubmitted = !!invoice;

  const formatPaymentType = (type) => {
    if (!type) return "-";
    return type
      .split("_")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const title = (() => {
    if (!state?.within3Days && state?.alreadyRequested) {
      return "Edit Not Allowed";
    }
    if (!state?.within3Days && !state?.alreadyRequested) {
      return "Request Not Allowed";
    }
    return "Request Not Allowed";
  })();

  return (
    <div className="page-wrapper">
      <div className="container">
        <header className="header">
          <h1>SPORTS WE PLAY SDN. BHD.</h1>
        </header>

        <main className="content">

          {/* ===== LOCK MESSAGE ===== */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <h2>{title}</h2>
            <p>
              {state?.message ||
                "You cannot request or edit an e-invoice after 3 days from payment date."}
            </p>
          </div>

          {/* ===== SUBMITTED INVOICE (LOCKED) ===== */}
          {hasSubmitted && (
            <div className="summary-wrapper">
              <div className="card">
                <h2 className="card-title">Submitted E-Invoice</h2>

                <table className="invoice-table">
                  <tbody>
                    <tr>
                      <td><b>Order ID</b></td>
                      <td>{invoice.order_id}</td>
                    </tr>
                    <tr>
                      <td><b>Name</b></td>
                      <td>{invoice.customer_name}</td>
                    </tr>
                    <tr>
                      <td><b>Email</b></td>
                      <td>{invoice.customer_email}</td>
                    </tr>
                    <tr>
                      <td><b>Contact</b></td>
                      <td>{invoice.customer_phone}</td>
                    </tr>
                    <tr>
                      <td><b>TIN</b></td>
                      <td>{invoice.customer_tin}</td>
                    </tr>
                    <tr>
                      <td><b>{invoice.id_type}</b></td>
                      <td>{invoice.customer_brn}</td>
                    </tr>
                    <tr>
                      <td><b>Address</b></td>
                      <td>
                        {invoice.address1}
                        {invoice.address2 && `, ${invoice.address2}`},{" "}
                        {invoice.postcode} {invoice.city}, {invoice.state}
                      </td>
                    </tr>
                    <tr>
                      <td><b>Payment Type</b></td>
                      <td>{formatPaymentType(invoice.payment_type)}</td>
                    </tr>
                    <tr>
                      <td><b>Payment Date</b></td>
                      <td>{order.update_date_time}</td>
                    </tr>
                    <tr>
                      <td><b>Total Paid</b></td>
                      <td>RM {invoice.total_payable}</td>
                    </tr>
                    <tr>
                      <td><b>Submitted At</b></td>
                      <td>{invoice.created_at}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== ORDER DETAILS ONLY (NO SUBMISSION) ===== */}
          {!hasSubmitted && order && (
            <div className="summary-wrapper">
              <div className="card">
                <h2 className="card-title">Order Details</h2>

                <table className="invoice-table">
                  <tbody>
                    <tr>
                      <td><b>Order ID</b></td>
                      <td>{order.order_id}</td>
                    </tr>
                    <tr>
                      <td><b>Payment Type</b></td>
                      <td>{formatPaymentType(order.payment_type)}</td>
                    </tr>
                    <tr>
                      <td><b>Total Paid</b></td>
                      <td>RM {order.total_payable}</td>
                    </tr>
                    <tr>
                      <td><b>Payment Date</b></td>
                      <td>{order.update_date_time}</td>
                    </tr>
                    <tr>
                      <td><b>Status</b></td>
                      <td>No e-invoice was requested</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </main>

        <footer className="footer">
          <p>© {new Date().getFullYear()} SPORTS WE PLAY SDN. BHD.</p>
        </footer>
      </div>
    </div>
  );
}
